// src/pages/OrderSuccess.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { formatEurCheckout } from '../lib/currency';

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get('id');
  const [order, setOrder] = useState(null);
  const navigate = useNavigate();
  const { user, showToast } = useApp();
  const spinGrantedRef = useRef(false);

  useEffect(() => {
    if (!orderId) return;
    supabase.from('orders').select('*').eq('id', orderId).single()
      .then(({ data }) => setOrder(data));
  }, [orderId]);

  // Grant +1 spin after a paid order — once per order, race-condition safe
  useEffect(() => {
    if (!order || !user || spinGrantedRef.current) return;
    // Only grant on paid/delivered — not on pending
    if (!['paid', 'delivered'].includes(order.status)) return;
    // Already granted for this order
    if (order.spin_reward_granted) return;

    spinGrantedRef.current = true;

    const grantSpin = async () => {
      try {
        // 1. Mark order so we never double-grant
        await supabase.from('orders')
          .update({ spin_reward_granted: true })
          .eq('id', orderId)
          .eq('spin_reward_granted', false); // atomic guard

        // 2. Read current balance then increment (no RPC needed)
        const { data: prof } = await supabase
          .from('profiles')
          .select('spin_balance')
          .eq('id', user.id)
          .single();

        if (prof) {
          await supabase.from('profiles')
            .update({ spin_balance: (prof.spin_balance || 0) + 1 })
            .eq('id', user.id);
          showToast('🎰 +1 spin added for your order!', 'success');
        }
      } catch (err) {
        console.error('[OrderSuccess] spin grant failed:', err.message);
      }
    };

    grantSpin();
  }, [order, user, orderId, showToast]);

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'6rem 2rem', textAlign:'center', minHeight:'60vh' }}>
      <div style={{ fontSize:'4.5rem', marginBottom:'1.5rem' }}>✅</div>
      <h1 style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'2.5rem', letterSpacing:'.05em', color:'#4cca74', marginBottom:10 }}>Order Confirmed!</h1>
      {order && (
        <>
          <p style={{ color:'#777', fontSize:'.9rem', marginBottom:4 }}>Order #{order.id.slice(0,8).toUpperCase()}</p>
          <p style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.6rem', letterSpacing:'.04em', color:'#f4f4f4', marginBottom:8 }}>{formatEurCheckout(order.total_eur)}</p>
          {order.loyalty_points_earned > 0 && (
            <p style={{ color:'#4cca74', fontSize:'.88rem', marginBottom:8 }}>+{order.loyalty_points_earned} loyalty points earned!</p>
          )}
          <p style={{ color:'#4cca74', fontSize:'.88rem', marginBottom:16 }}>🎰 Check your Reward Wheel for a free spin!</p>
        </>
      )}
      <p style={{ color:'#555', fontSize:'.88rem', lineHeight:1.9, maxWidth:380, marginBottom:'2.5rem' }}>
        A confirmation email has been sent. Your order will be dispatched within 1–2 business days in discreet packaging.
      </p>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', justifyContent:'center' }}>
        <button className="btn-primary" onClick={() => navigate('/')}>Continue Shopping</button>
        <button className="btn-ghost" onClick={() => navigate('/wheel')}>Spin the Wheel 🎰</button>
        <button className="btn-ghost" onClick={() => navigate('/orders')}>View Orders</button>
      </div>
    </div>
  );
}